import React from 'react';
import { find, isNil, noop, isEmpty } from 'lodash';

// import default view
import { singleSelect as SingleSelectView } from './singleSelect.view';
import { popover } from 'modules/popover';
import { ChoiceList } from 'modules/choice-list';
import { DATA_TYPES } from 'constants/common';

/**
 * @type {function}
 * @desc A React container/smart component to render a React presentational/dumb component (view).
 * @param {object} Component - A React component
 * @example
 * import { SingleSelect } from 'modules/singleSelect';
 *
 * // render
 * <SingleSelect propName='propValue'/>
 */
export class SingleSelect extends React.Component {
    constructor( props ) {
        super( props );

        // bind methods to instance
        this.handlePopoverOpen = this.handlePopoverOpen.bind( this );
        this.handleOptionSelection = this.handleOptionSelection.bind( this );

        // component state
        this.state = {
            isPopoverOpen: false,
        };

        // trigger element reference
        this.triggerRef = React.createRef();
    }

    /*********************************************/

    /**
     * @desc Update `selected` options in the state and call the `onChange` callback function.
     */
    handleOptionSelection( value ) {
        log.debug( 'SingleSelect.handleOptionSelection()' );

        // find selected option
        const selectedOption = find( this.props.options, { value } );

        this.setState( {
            isPopoverOpen: false,
        }, () => {

            // close popover
            popover.close();

            // execute `onChange` callback
            this.props.onChange( {
                option: selectedOption,
                value: value,
            } );
        } );
    }

    /**
     * @desc Open `popover` to display options.
     */
    handlePopoverOpen() {
        log.debug( 'SingleSelect.handlePopoverOpen()' );
        
        // open popover with options
        if( ! this.state.isPopoverOpen ) {
            this.setState( {
                isPopoverOpen: true,
            }, () => {
                popover.open( {
                    referenceElement: this.triggerRef.current,
                    view: ChoiceList,
                    tooltip: false,
                    compact: isNil( this.props.popupWidth ),
                    styles: {
                        width: this.props.popupWidth,
                    },
                    props: {
                        allowFilter: this.props.allowFilter,
                        displaySelectAllOption: this.props.displaySelectAllOption,
                        type: this.props.type,
                        options: this.props.options,
                        selected: [ isEmpty( this.props.value ) ? null : this.props.value ],
                        onChange: this.handleOptionSelection,
                    },
                    onClose: () => this.setState( { isPopoverOpen: false } )
                } );
            } );
        }
    }

    /*********************************************/

    render() {
        return (
            <SingleSelectView
                ref={ this.triggerRef }
                options={ this.props.options }
                disabled={ this.props.disabled }
                className={ this.props.className }
                value={ isEmpty( this.props.value ) ? null : this.props.value }
                isPopoverOpen={ this.state.isPopoverOpen }
                onPopoverOpen={ this.handlePopoverOpen }
            />
        );
    }
}

/*********************************************/

// set display name
SingleSelect.displayName = 'SingleSelect';

// default props
SingleSelect.defaultProps = {
    options: [],
    value: null,
    onChange: noop,
    popupWidth: null,
    allowFilter: false,
    placeholder: undefined,
    displaySelectAllOption: false,
    type: DATA_TYPES.SINGLE_SELECT,
    disabled: false,
};

// prop types
SingleSelect.propTypes = {};
